import { FC, useEffect, useMemo, useState } from 'react';
import './BucketTreePicker.scss';

export interface BucketNode {
    bucket_id: number;
    name: string;
    parent_bucket: number | null;
    user_count: number;
}

export interface TreeNode extends BucketNode {
    children: TreeNode[];
    expanded: boolean;
}

interface BucketTreePickerProps {
    buckets: BucketNode[];
    selectedBucketIds: number[];
    onSelectionChange: (bucketIds: number[]) => void;
    loading?: boolean;
    error?: string | null;
}

/**
 * Builds a tree structure from a flat list of bucket nodes.
 */
function buildTree(buckets: BucketNode[]): TreeNode[] {
    const nodeMap = new Map<number, TreeNode>();
    const roots: TreeNode[] = [];

    // Create TreeNode for each bucket
    for (const bucket of buckets) {
        nodeMap.set(bucket.bucket_id, {
            ...bucket,
            children: [],
            expanded: false,
        });
    }

    // Link children to parents
    for (const bucket of buckets) {
        const node = nodeMap.get(bucket.bucket_id)!;
        if (bucket.parent_bucket === null || !nodeMap.has(bucket.parent_bucket)) {
            roots.push(node);
        } else {
            const parent = nodeMap.get(bucket.parent_bucket)!;
            parent.children.push(node);
        }
    }

    return roots;
}

const BucketTreePicker: FC<BucketTreePickerProps> = ({
    buckets,
    selectedBucketIds,
    onSelectionChange,
    loading = false,
    error = null,
}) => {
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    const tree = useMemo(() => buildTree(buckets), [buckets]);

    // Auto-expand root nodes on first load
    useEffect(() => {
        if (tree.length > 0 && expandedIds.size === 0) {
            setExpandedIds(new Set(tree.map(n => n.bucket_id)));
        }
    }, [tree]);

    const toggleExpand = (bucketId: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(bucketId)) {
                next.delete(bucketId);
            } else {
                next.add(bucketId);
            }
            return next;
        });
    };

    const toggleSelect = (bucketId: number) => {
        const isSelected = selectedBucketIds.includes(bucketId);
        if (isSelected) {
            onSelectionChange(selectedBucketIds.filter(id => id !== bucketId));
        } else {
            if (selectedBucketIds.length >= 50) return; // Max 50 buckets
            onSelectionChange([...selectedBucketIds, bucketId]);
        }
    };

    const matchesSearch = (node: TreeNode): boolean => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        if (node.name.toLowerCase().includes(term)) return true;
        return node.children.some(child => matchesSearch(child));
    };

    const renderNode = (node: TreeNode, depth: number = 0): JSX.Element | null => {
        if (!matchesSearch(node)) return null;

        const hasChildren = node.children.length > 0;
        const isExpanded = expandedIds.has(node.bucket_id);
        const isSelected = selectedBucketIds.includes(node.bucket_id);

        return (
            <div key={node.bucket_id} className="bucket-tree-node">
                <div
                    className={`bucket-tree-row ${isSelected ? 'selected' : ''}`}
                    style={{ paddingLeft: `${depth * 20 + 8}px` }}
                >
                    <span
                        className={`bucket-tree-expand ${hasChildren ? 'has-children' : ''}`}
                        onClick={() => hasChildren && toggleExpand(node.bucket_id)}
                    >
                        {hasChildren ? (isExpanded ? '▼' : '▶') : '•'}
                    </span>
                    <label className="bucket-tree-label">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(node.bucket_id)}
                            className="bucket-tree-checkbox"
                        />
                        <span className="bucket-tree-name">{node.name}</span>
                        <span className="bucket-tree-count">({node.user_count})</span>
                    </label>
                </div>
                {hasChildren && isExpanded && (
                    <div className="bucket-tree-children">
                        {node.children.map(child => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return <div className="bucket-tree-picker loading">Loading bucket hierarchy...</div>;
    }

    if (error) {
        return <div className="bucket-tree-picker error">{error}</div>;
    }

    if (buckets.length === 0) {
        return <div className="bucket-tree-picker empty">No buckets available</div>;
    }

    return (
        <div className="bucket-tree-picker">
            <div className="bucket-tree-search">
                <input
                    type="text"
                    placeholder="Search buckets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bucket-tree-search-input"
                />
            </div>
            <div className="bucket-tree-container">
                {tree.map(node => renderNode(node, 0))}
            </div>
            {selectedBucketIds.length > 0 && (
                <div className="bucket-tree-selection-info">
                    {selectedBucketIds.length} bucket{selectedBucketIds.length !== 1 ? 's' : ''} selected
                    {selectedBucketIds.length >= 50 && <span className="bucket-tree-max-warning"> (max reached)</span>}
                </div>
            )}
        </div>
    );
};

export default BucketTreePicker;
